"use client"

import { useEffect, useState } from "react"
import { X, Share, PlusSquare, MoreHorizontal } from "lucide-react"

export default function AddToHomeScreen() {
  const [showButton, setShowButton] = useState(false)
  const [showIOSSheet, setShowIOSSheet] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<Event & { prompt: () => void } | null>(null)
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    // Don't show if already running as installed PWA
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      ("standalone" in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true)

    if (isStandalone) return

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
    setIsIOS(ios)

    if (ios) {
      // Show the button on iOS Safari (but not Chrome/Firefox on iOS which can't install PWAs)
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
      if (isSafari) setShowButton(true)
      return
    }

    // Android / desktop Chrome: capture the beforeinstallprompt event
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as Event & { prompt: () => void })
      setShowButton(true)
    }
    window.addEventListener("beforeinstallprompt", handler)
    return () => window.removeEventListener("beforeinstallprompt", handler)
  }, [])

  const handleClick = async () => {
    if (isIOS) {
      setShowIOSSheet(true)
      return
    }
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    setDeferredPrompt(null)
    setShowButton(false)
  }

  if (!showButton) return null

  return (
    <>
      <button
        onClick={handleClick}
        className="md:hidden flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
      >
        <PlusSquare className="h-3.5 w-3.5" />
        Add App
      </button>

      {/* iOS instruction sheet — z-[60] to sit above BottomTabBar (z-50) */}
      {showIOSSheet && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-end"
          onClick={() => setShowIOSSheet(false)}
        >
          <div
            className="w-full bg-white rounded-t-2xl shadow-2xl p-6 overflow-y-auto"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)", maxHeight: "70dvh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Add to Home Screen</h2>
              <button onClick={() => setShowIOSSheet(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Install Blue Collar Social for quick access and push notifications.
            </p>
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <p className="text-sm text-gray-700">
                  Tap the{" "}
                  <span className="inline-flex items-center gap-0.5 font-medium">
                    <MoreHorizontal className="h-4 w-4" />
                  </span>{" "}
                  <span className="font-medium">···</span> button at the bottom-right of the Safari toolbar
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  Tap{" "}
                  <span className="inline-flex items-center gap-1 font-medium text-blue-600">
                    <Share className="h-4 w-4" /> Share
                  </span>{" "}
                  from the menu
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <p className="text-sm text-gray-700">
                  Scroll down in the share sheet and tap{" "}
                  <span className="font-medium">&ldquo;Add to Home Screen&rdquo;</span>
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                <p className="text-sm text-gray-700">
                  Tap <span className="font-medium">&ldquo;Add&rdquo;</span> in the top-right corner to confirm
                </p>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  )
}
